import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Chip,
  Divider,
  Button,
  TextField,
  FormControl,
  FormLabel,
  Fab,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FONT = "'Inter', 'Roboto', sans-serif";

const FALLBACK = {
  staffId: "STF001",
  fullName: "Meena Rajan",
  email: "meena.rajan@cummingstamilschool.org",
  role: "Staff",
  designation: "Administrative Assistant",
  department: "Administration",
  address: "456 Oak Avenue, Atlanta, GA 30302",
  contactNumber: "+1 (678) 555-0123",
  alternateContact: "+1 (678) 555-0124",
  joiningDate: "2021-09-01",
  photo: null,
};

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
    "&:hover fieldset": { borderColor: "#4A7BA8" },
    "&.Mui-focused fieldset": { borderColor: "#244C77" },
  },
};

function InfoRow({ icon, label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        py: 1.2,
        borderBottom: "1px solid #F3F4F6",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          color: "#4A7BA8",
          mt: 0.2,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: "11px", color: "#9CA3AF", fontFamily: FONT }}>{label}</Typography>
        <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 500, fontFamily: FONT, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function EditField({ label, name, value, onChange, multiline, type = "text" }) {
  return (
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth>
        <FormLabel sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>{label}</FormLabel>
        <TextField
          size="small"
          name={name}
          type={type}
          value={value || ""}
          onChange={onChange}
          multiline={multiline}
          rows={multiline ? 3 : undefined}
          InputLabelProps={type === "date" ? { shrink: true } : undefined}
          sx={FIELD_SX}
        />
      </FormControl>
    </Grid>
  );
}

export default function StaffProfile() {
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const staffId =
    sessionStorage.getItem("userId") ||
    sessionStorage.getItem("applicationId") ||
    "";

  const load = useCallback(async () => {
    try {
      if (!staffId) { setProfile(FALLBACK); return; }
      const res = await fetchData("staff/profile");
      if (res?.data) setProfile(res.data);
      else setProfile(FALLBACK);
    } catch {
      setProfile(FALLBACK);
    }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const p = profile || FALLBACK;

  const initials = (p.fullName || "SR")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
    } catch { return d; }
  };

  const handleEditToggle = () => {
    setEditValues({ ...p });
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditValues({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        address: editValues.address,
        contactNumber: editValues.contactNumber,
        alternateContact: editValues.alternateContact,
      };
      await putData("staff/profile", payload);
      setProfile((prev) => ({ ...prev, ...payload }));
      setToast({ open: true, severity: "success", message: "Profile updated successfully!" });
      setEditMode(false);
      setEditValues({});
    } catch {
      // optimistic update
      setProfile((prev) => ({
        ...prev,
        address: editValues.address,
        contactNumber: editValues.contactNumber,
        alternateContact: editValues.alternateContact,
      }));
      setToast({ open: true, severity: "warning", message: "Changes saved locally (server unavailable)." });
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        padding: "1.5rem",
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        textAlign: "left",
        fontFamily: FONT,
      }}
    >
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>
        Pages / My Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Left: Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            }}
          >
            {/* Banner */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                p: 3,
                textAlign: "center",
              }}
            >
              <Avatar
                src={p.photo || undefined}
                sx={{
                  width: 84,
                  height: 84,
                  mx: "auto",
                  mb: 1.5,
                  border: "3px solid rgba(255,255,255,0.4)",
                  fontSize: "26px",
                  fontWeight: "bold",
                  backgroundColor: "#4A7BA8",
                  fontFamily: FONT,
                }}
              >
                {initials}
              </Avatar>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "18px", fontFamily: FONT }}>
                {p.fullName || "Staff Member"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", mt: 0.3 }}>
                {p.designation || p.role || "Staff"}
              </Typography>
              <Chip
                label={`ID: ${p.staffId || "STF001"}`}
                size="small"
                sx={{
                  mt: 1.5,
                  backgroundColor: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            </Box>

            {/* Quick stats */}
            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  { label: "Role", value: p.role || "Staff" },
                  { label: "Department", value: p.department || "—" },
                  { label: "Joined", value: p.joiningDate ? new Date(p.joiningDate).getFullYear() : "—" },
                ].map((s) => (
                  <Grid item xs={4} key={s.label}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        textAlign: "center",
                        borderRadius: "10px",
                        backgroundColor: "#F9FAFB",
                      }}
                    >
                      <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#244C77", fontFamily: FONT }}>
                        {s.value}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: "#6B7280", fontFamily: FONT }}>
                        {s.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mb: 1.5 }} />
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                <strong style={{ color: "#374151" }}>Joined: </strong>
                {formatDate(p.joiningDate)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Details */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            }}
          >
            {/* Gradient section header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px", fontFamily: FONT }}>
                Staff Information
              </Typography>
              {!editMode && (
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon />}
                  onClick={handleEditToggle}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.5)",
                    border: "1px solid rgba(255,255,255,0.5)",
                    textTransform: "none",
                    borderRadius: "8px",
                    fontSize: "12px",
                    "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </Box>

            <Box sx={{ p: 3 }}>
              {editMode && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 3 }}>
                  <Button
                    startIcon={<CloseIcon />}
                    size="small"
                    onClick={handleCancel}
                    disabled={saving}
                    sx={{
                      border: "1px solid #D5D7DA",
                      borderRadius: "8px",
                      color: "#374151",
                      textTransform: "none",
                      fontSize: "12px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    startIcon={<SaveOutlinedIcon />}
                    size="small"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      background: "linear-gradient(90deg, #4A7BA8, #244C77)",
                      color: "#fff",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "12px",
                      "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
                    }}
                  >
                    {saving ? "Saving…" : "Update"}
                  </Button>
                </Box>
              )}

              {!editMode ? (
                <>
                  {/* Read-only fields */}
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#4A7BA8", mb: 1, letterSpacing: 1, textTransform: "uppercase" }}>
                    Identity
                  </Typography>
                  <InfoRow icon={<BadgeIcon sx={{ fontSize: "16px" }} />} label="Staff ID" value={p.staffId} />
                  <InfoRow icon={<EditOutlinedIcon sx={{ fontSize: "16px" }} />} label="Full Name" value={p.fullName} />
                  <InfoRow icon={<EmailIcon sx={{ fontSize: "16px" }} />} label="Email (read-only)" value={p.email} />
                  <InfoRow icon={<WorkIcon sx={{ fontSize: "16px" }} />} label="Role / Designation" value={`${p.role || ""}${p.designation ? ` · ${p.designation}` : ""}`} />
                  <InfoRow icon={<BusinessIcon sx={{ fontSize: "16px" }} />} label="Department (read-only)" value={p.department} />

                  <Divider sx={{ my: 2 }} />

                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#4A7BA8", mb: 1, letterSpacing: 1, textTransform: "uppercase" }}>
                    Contact
                  </Typography>
                  <InfoRow icon={<HomeIcon sx={{ fontSize: "16px" }} />} label="Address" value={p.address} />
                  <InfoRow icon={<PhoneIcon sx={{ fontSize: "16px" }} />} label="Contact Number" value={p.contactNumber} />
                  <InfoRow icon={<PhoneIcon sx={{ fontSize: "16px" }} />} label="Alternate Contact" value={p.alternateContact} />
                </>
              ) : (
                <>
                  {/* Read-only section */}
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#4A7BA8", mb: 1.5, letterSpacing: 1, textTransform: "uppercase" }}>
                    Identity (read-only)
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { label: "Staff ID", value: p.staffId },
                      { label: "Full Name", value: p.fullName },
                      { label: "Email", value: p.email },
                      { label: "Role", value: p.role },
                      { label: "Designation", value: p.designation },
                      { label: "Department", value: p.department },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>{label}</FormLabel>
                          <TextField
                            size="small"
                            value={value || ""}
                            InputProps={{ readOnly: true }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "#F9FAFB",
                                "& fieldset": { borderColor: "#E5E7EB", borderRadius: "10px" },
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ mb: 2 }} />

                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#4A7BA8", mb: 1.5, letterSpacing: 1, textTransform: "uppercase" }}>
                    Editable Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <FormLabel sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Address</FormLabel>
                        <TextField
                          size="small"
                          name="address"
                          multiline
                          rows={2}
                          value={editValues.address || ""}
                          onChange={handleChange}
                          sx={FIELD_SX}
                        />
                      </FormControl>
                    </Grid>
                    <EditField label="Contact Number" name="contactNumber" value={editValues.contactNumber} onChange={handleChange} />
                    <EditField label="Alternate Contact" name="alternateContact" value={editValues.alternateContact} onChange={handleChange} />
                  </Grid>
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* FAB for edit */}
      {!editMode && (
        <Fab
          size="medium"
          onClick={handleEditToggle}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            background: "linear-gradient(135deg, #4A7BA8, #244C77)",
            color: "#fff",
            boxShadow: "0 4px 16px rgba(80,56,237,0.4)",
            "&:hover": { background: "linear-gradient(135deg, #4A7BA8, #244C77)" },
          }}
        >
          <EditOutlinedIcon />
        </Fab>
      )}
    </Box>
  );
}
